import { useState, useEffect, useCallback } from 'react';
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/services/firebase';
import type { AuthUser } from '@/types/auth';

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Firebase recommends using onAuthStateChanged as the primary way to track auth state
  // It automatically handles persistence (IndexedDB by default) and fires on:
  // - Initial page load (with persisted user or null)
  // - Sign in
  // - Sign out
  // - Token refresh
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        setUser(firebaseUser as AuthUser | null);
        setLoading(false);
      },
      (err) => {
        console.error('Auth state error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const provider = new GoogleAuthProvider();
      // signInWithPopup returns UserCredential, but we don't need to use it
      // onAuthStateChanged will automatically fire with the new user
      await signInWithPopup(auth, provider);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setError(message);
      setLoading(false);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setError(null);
      await signOut(auth);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign out';
      setError(message);
      throw err;
    }
  }, []);

  const getIdToken = useCallback(async () => {
    if (!user) {
      throw new Error('Not authenticated');
    }
    return user.getIdToken();
  }, [user]);

  return {
    user,
    loading,
    error,
    signInWithGoogle,
    logout,
    getIdToken,
  };
}
